// Admin → User detail. Profile + tenancy memberships + sensitive actions
// (edit / suspend / reactivate / reset password / impersonate). Every action is
// audited server-side. docs/replan-v2.md (admin console). English chrome.
//
// The edit affordance is the SAME `UserEditDialog` the family drill-in uses —
// one form, one set of guard rails, one consequence banner. This page had none
// of it until now: `PATCH /admin/users/:id` and the dialog both shipped, but
// the only door into them was the family page, so a staff member who reached a
// user from Users could read the record and never change it.

import { Alert, Badge, Button } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { Icon, PageHeader, StatusBadge } from '~/components';
import { UserEditDialog } from '~/components/family/UserEditDialog';
import {
  getUser,
  impersonateUser,
  reactivateUser,
  resetUserPassword,
  suspendUser,
} from '~/lib/admin';
import { ApiError } from '~/lib/api';

export const Route = createFileRoute('/_authed/users/$id')({
  component: UserDetailPage,
});

// The consumer app origin — where an impersonation session lands.
function appOrigin(): string {
  if (typeof window !== 'undefined' && window.location.hostname.startsWith('staging-')) {
    return 'https://staging-app.fajrarabi.com';
  }
  return 'https://app.fajrarabi.com';
}

function UserDetailPage() {
  const { id } = useParams({ from: '/_authed/users/$id' });
  const qc = useQueryClient();
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const userQ = useQuery({ queryKey: ['user', id], queryFn: ({ signal }) => getUser(id, signal) });
  const u = userQ.data;

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['user', id] });
    void qc.invalidateQueries({ queryKey: ['users'] });
  };
  const onErr = (e: unknown) =>
    setError(e instanceof ApiError ? e.message : 'Something went wrong.');

  const suspend = useMutation({
    mutationFn: () => suspendUser(id),
    onSuccess: invalidate,
    onError: onErr,
  });
  const reactivate = useMutation({
    mutationFn: () => reactivateUser(id),
    onSuccess: invalidate,
    onError: onErr,
  });
  const reset = useMutation({
    mutationFn: () => resetUserPassword(id),
    onSuccess: (pw) => {
      setTempPassword(pw);
      invalidate();
    },
    onError: onErr,
  });
  const impersonate = useMutation({
    mutationFn: () => impersonateUser(id),
    onSuccess: () => {
      // The admin's cookie is now the target's read-only session — land in the app.
      window.location.href = `${appOrigin()}/ar`;
    },
    onError: onErr,
  });

  if (userQ.isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="h-8 w-56 animate-pulse rounded-lg bg-admin-100" />
        <div className="h-40 animate-pulse rounded-2xl bg-admin-100" />
      </div>
    );
  }
  if (!u) return <div className="mx-auto max-w-3xl text-sm text-heart">User not found.</div>;

  const name = u.displayName ?? u.email ?? u.username ?? u.id.slice(0, 8);
  const isStaff = u.roles.some(
    (r) => r.includes('admin') || r === 'content_editor' || r === 'support',
  );

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={name}
        description={u.email ?? (u.username ? `@${u.username}` : 'No login identity on file.')}
        crumbs={[{ label: 'Users', to: '/users' }, { label: name }]}
        badge={<StatusBadge status={u.status} dot />}
        actions={
          <Button variant="secondary" onClick={() => setEditOpen(true)}>
            <Icon name="edit" className="me-1.5 h-4 w-4" />
            Edit user
          </Button>
        }
      />

      {u.roles.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {u.roles.map((r) => (
            <Badge key={r} tone={r.includes('admin') ? 'brand' : 'neutral'}>
              {r}
            </Badge>
          ))}
        </div>
      )}

      {error && (
        <Alert tone="danger" className="mt-4">
          {error}
        </Alert>
      )}
      {tempPassword && (
        <Alert tone="success" className="mt-4">
          New password (shown once — copy it now): <strong>{tempPassword}</strong>
        </Alert>
      )}

      {/* Profile */}
      <section className="mt-6 grid gap-4 rounded-2xl border border-burgundy-500/10 bg-white p-5 sm:grid-cols-2">
        <Field label="Email">
          {u.email ?? '—'}
          {u.email && !u.emailVerified ? ' (unverified)' : ''}
        </Field>
        <Field label="Username">{u.username ? `@${u.username}` : '—'}</Field>
        <Field label="Display name">{u.displayName ?? '—'}</Field>
        <Field label="Phone">{u.phone ?? '—'}</Field>
        <Field label="Locale">{u.locale}</Field>
        <Field label="Country">{u.country ?? '—'}</Field>
        <Field label="Joined">{u.createdAt.slice(0, 10)}</Field>
        <Field label="User ID">
          <code className="text-xs">{u.id}</code>
        </Field>
      </section>

      {/* Memberships */}
      {(u.families.length > 0 || u.schools.length > 0) && (
        <section className="mt-4 rounded-2xl border border-burgundy-500/10 bg-white p-5">
          <p className="text-xs uppercase tracking-wider text-ink-500">Memberships</p>
          <ul className="mt-2 space-y-1 text-sm text-ink-700">
            {u.families.map((f) => (
              <li key={f.id}>
                Family <code className="text-xs">{f.id.slice(0, 8)}</code> · {f.role}
              </li>
            ))}
            {u.schools.map((s) => (
              <li key={s.id}>
                School &ldquo;{s.name}&rdquo; · {s.role}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Actions */}
      <section className="mt-6 flex flex-wrap gap-3">
        {u.status === 'active' ? (
          <Button
            variant="secondary"
            onClick={() => suspend.mutate()}
            disabled={suspend.isPending || isStaff}
          >
            Suspend
          </Button>
        ) : (
          <Button
            variant="secondary"
            onClick={() => reactivate.mutate()}
            disabled={reactivate.isPending}
          >
            Reactivate
          </Button>
        )}
        <Button variant="secondary" onClick={() => reset.mutate()} disabled={reset.isPending}>
          Reset password
        </Button>
        <Button
          onClick={() => impersonate.mutate()}
          disabled={impersonate.isPending || u.status !== 'active'}
        >
          View as user (read-only)
        </Button>
      </section>
      {isStaff && (
        <p className="mt-2 text-xs text-ink-500">
          Staff accounts can&rsquo;t be suspended from here.
        </p>
      )}

      <UserEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        user={u}
        // The list caches the same name/status this page just changed, so it is
        // refreshed alongside the row itself or Users shows a stale record.
        invalidateKeys={[['users']]}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-ink-500">{label}</p>
      <p className="mt-0.5 text-ink-900">{children}</p>
    </div>
  );
}
